import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  ButtonGroup,
  Box,
  Alert,
} from "@mui/material";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useMarketplaceStore } from "../../../../../store/useMarketplaceStore";
import { useTranslation } from "../../../../../hooks/useTranslation";
import LoadingButton from "@mui/lab/LoadingButton";

interface FormValues {
  list: string;
  asinOrSku: "asin" | "sku";
}

interface DeleteProductDialogProps {
  open: boolean;
  onClose: () => void;
}

export const DeleteProductDialog: React.FC<DeleteProductDialogProps> = ({
  open,
  onClose,
}) => {
  const {
    reset,
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      list: "",
      asinOrSku: "asin",
    },
  });

  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.inventoryManagement.inventory.actions.deleteProducts",
  );
  const { selectedCountry } = useMarketplaceStore((state) => state);
  const { mutate, isLoading } = useCustomMutation();
  const invalidate = useInvalidate();

  const [deleteResult, setDeleteResult] = useState<{
    totalProducts: number;
    deletedCount: number;
    alreadyDeletedCount: number;
    failedCount: number;
    sentToAmazonRemovingQueue: number;
  } | null>(null);

  const onSubmit = (data: FormValues) => {
    const inputToArray = data.list.split("\n").map((val) => val.trim());
    const values: { asinList?: string[]; skuList?: string[] } = {};

    if (data.asinOrSku === "asin") {
      values["asinList"] = inputToArray;
    } else {
      values["skuList"] = inputToArray;
    }

    mutate(
      {
        url: `store/${selectedCountry?.id}/product/delete`,
        method: "delete",
        values: values,
        errorNotification: () => ({
          message: translate("deleteError"),
          type: "error",
        }),
      },
      {
        onSuccess: (responseData) => {
          const result = responseData.data as {
            totalProducts: number;
            deletedCount: number;
            alreadyDeletedCount: number;
            failedCount: number;
            sentToAmazonRemovingQueue: number;
          };
          handleClose();
          setDeleteResult(result);

          setTimeout(
            () =>
              invalidate({
                resource: "all",
                invalidates: ["all"],
              }),
            500,
          );
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <Stack direction="row" justifyContent="center">
          <DialogTitle>{translate("title")}</DialogTitle>
        </Stack>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {translate("warning")}
            </Alert>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography mr={2}>{translate("asinOrSku")}</Typography>
              <Controller
                name="asinOrSku"
                control={control}
                render={({ field }) => (
                  <ButtonGroup variant="outlined">
                    <Button
                      size="small"
                      variant={
                        field.value === "asin" ? "contained" : "outlined"
                      }
                      onClick={() => field.onChange("asin")}
                      sx={{
                        textTransform: "capitalize",
                        whiteSpace: "noWrap",
                      }}
                    >
                      ASIN
                    </Button>
                    <Button
                      size="small"
                      variant={field.value === "sku" ? "contained" : "outlined"}
                      onClick={() => field.onChange("sku")}
                      sx={{
                        textTransform: "capitalize",
                        whiteSpace: "noWrap",
                      }}
                    >
                      SKU
                    </Button>
                  </ButtonGroup>
                )}
              />
            </Box>

            <Controller
              name="asinOrSku"
              control={control}
              render={({ field }) => (
                <>
                  <Typography variant="h6">
                    {translate("listTitle", {
                      type: field.value.toUpperCase(),
                    })}
                  </Typography>

                  <TextField
                    placeholder={translate("listHint", {
                      type: field.value.toUpperCase(),
                    })}
                    multiline
                    rows={8}
                    fullWidth
                    variant="outlined"
                    {...register("list", {
                      required: translateWithoutPrefix(
                        "common.errors.required",
                      ),
                      validate: (value) =>
                        value.split("\n").length <= 5000 ||
                        translate("max5000"),
                    })}
                    error={!!errors.list}
                    helperText={errors.list?.message}
                  />
                </>
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>
              {translateWithoutPrefix("buttons.cancel")}
            </Button>
            <LoadingButton
              loading={isLoading}
              disabled={isLoading || !isDirty || !isValid}
              variant="contained"
              type="submit"
            >
              {translate("deleteButton")}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Silme İşlemi Sonuç Dialog'u */}
      <Dialog
        open={!!deleteResult}
        onClose={() => setDeleteResult(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{translate("deleteResultTitle")}</DialogTitle>
        <DialogContent>
          {(deleteResult?.totalProducts ?? 0) > 0 && (
            <Typography>
              {translate("totalProducts", {
                count: deleteResult?.totalProducts,
              })}
            </Typography>
          )}
          {(deleteResult?.deletedCount ?? 0) > 0 && (
            <Typography>
              {translate("deletedCount", { count: deleteResult?.deletedCount })}
            </Typography>
          )}
          {(deleteResult?.sentToAmazonRemovingQueue ?? 0) > 0 && (
            <Typography>
              {translate("sentToAmazonRemovingQueue", {
                count: deleteResult?.sentToAmazonRemovingQueue,
              })}
            </Typography>
          )}
          {(deleteResult?.alreadyDeletedCount ?? 0) > 0 && (
            <Typography>
              {translate("alreadyDeletedCount", {
                count: deleteResult?.alreadyDeletedCount,
              })}
            </Typography>
          )}
          {(deleteResult?.failedCount ?? 0) > 0 && (
            <Typography>
              {translate("failedCount", { count: deleteResult?.failedCount })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteResult(null);
            }}
            variant="contained"
          >
            {translateWithoutPrefix("buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
